import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { fetchCustomerDetail, updateCustomer, deleteCustomer, fetchCustomerOrders, fetchCustomerBoughtProducts, createCustomerIncome, createCustomerDebt } from '@/services/customers'
import type { Customer, UpdateCustomerPayload } from '@/types/customers'
import type { CustomerOrder, CustomerBoughtProduct } from '@/services/customers'
import {
  AlertCircle,
  ArrowLeft,
  Phone,
  Calendar,
  DollarSign,
  ShoppingCart,
  User,
  UserX,
  Pencil,
  Trash2,
  Plus,
} from 'lucide-react'

function LoadingSkeleton() {
  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <div className='flex items-center gap-4'>
            <Skeleton className='h-16 w-16 rounded-full' />
            <div className='space-y-2'>
              <Skeleton className='h-6 w-48' />
              <Skeleton className='h-4 w-32' />
            </div>
          </div>
        </CardHeader>
      </Card>
      <div className='grid gap-4 md:grid-cols-3'>
        <Skeleton className='h-32' />
        <Skeleton className='h-32' />
        <Skeleton className='h-32' />
      </div>
    </div>
  )
}

export default function CustomerDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [orders, setOrders] = useState<CustomerOrder[]>([])
  const [boughtProducts, setBoughtProducts] = useState<CustomerBoughtProduct[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  // Edit sheet state
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false)
  const [formData, setFormData] = useState<UpdateCustomerPayload>({
    full_name: '',
    phone_number: '',
    total_given: undefined,
    total_debt: undefined,
  })

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  // Income sheet state
  const [isIncomeSheetOpen, setIsIncomeSheetOpen] = useState(false)
  const [incomeAmount, setIncomeAmount] = useState('')
  const [incomeDescription, setIncomeDescription] = useState('')
  const [isCreatingIncome, setIsCreatingIncome] = useState(false)

  // Debt sheet state
  const [isDebtSheetOpen, setIsDebtSheetOpen] = useState(false)
  const [debtAmount, setDebtAmount] = useState('')
  const [debtDescription, setDebtDescription] = useState('')
  const [isCreatingDebt, setIsCreatingDebt] = useState(false)
  const [debtError, setDebtError] = useState<string | null>(null)

  useEffect(() => {
    const loadCustomer = async () => {
      if (!id) return

      try {
        setIsLoading(true)
        setError(null)
        setNotFound(false)
        const [customerData, ordersData, boughtProductsData] = await Promise.all([
          fetchCustomerDetail(parseInt(id)),
          fetchCustomerOrders(parseInt(id)),
          fetchCustomerBoughtProducts(parseInt(id)),
        ])
        setCustomer(customerData)
        setOrders(ordersData)
        setBoughtProducts(boughtProductsData)
      } catch (err: unknown) {
        // Check if it's an axios error with response data
        const axiosError = err as { response?: { data?: { message?: string } } }
        const message = axiosError?.response?.data?.message

        if (message?.toLowerCase().includes('no customer matches')) {
          setNotFound(true)
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load customer details')
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadCustomer()
  }, [id])

  const openEditSheet = () => {
    if (customer) {
      setFormData({
        full_name: customer.full_name ?? '',
        phone_number: customer.phone_number ?? '',
        total_given: customer.total_given,
        total_debt: customer.total_debt,
      })
      setIsEditSheetOpen(true)
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !customer) return

    try {
      setIsUpdating(true)
      setError(null)
      const updatedCustomer = await updateCustomer(parseInt(id), formData)
      setCustomer(updatedCustomer)
      setIsEditSheetOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update customer')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    try {
      setIsDeleting(true)
      setError(null)
      await deleteCustomer(parseInt(id))
      navigate('/customers')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete customer')
      setIsDeleting(false)
    }
  }

  const handleCreateIncome = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !customer) return

    try {
      setIsCreatingIncome(true)
      setError(null)
      await createCustomerIncome(parseInt(id), {
        amount: incomeAmount,
        description: incomeDescription || null,
      })
      // Refresh customer data to get updated totals
      const updatedCustomer = await fetchCustomerDetail(parseInt(id))
      setCustomer(updatedCustomer)
      setIsIncomeSheetOpen(false)
      setIncomeAmount('')
      setIncomeDescription('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create income')
    } finally {
      setIsCreatingIncome(false)
    }
  }

  const handleCreateDebt = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !customer) return

    const amount = parseFloat(debtAmount)
    if (isNaN(amount) || amount <= 0) {
      setDebtError(t('customers.invalidDebtAmount'))
      return
    }

    try {
      setIsCreatingDebt(true)
      setDebtError(null)
      await createCustomerDebt(parseInt(id), {
        amount: debtAmount,
        description: debtDescription || null,
      })
      // Refresh customer data to get updated totals
      const updatedCustomer = await fetchCustomerDetail(parseInt(id))
      setCustomer(updatedCustomer)
      setIsDebtSheetOpen(false)
      setDebtAmount('')
      setDebtDescription('')
      setDebtError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create debt')
    } finally {
      setIsCreatingDebt(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '?'
    const parts = name.split(' ')
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
    }
    return name.charAt(0).toUpperCase()
  }

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/customers'>{t('customers.title')}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {customer ? (customer.full_name ?? '-') : t('common.loading')}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        <div className='flex items-center gap-2'>
          <Button variant='ghost' size='sm' asChild>
            <Link to='/customers'>
              <ArrowLeft className='mr-2 h-4 w-4' />
              {t('customers.backToCustomers')}
            </Link>
          </Button>
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : customer ? (
          <>
            {/* Profile Header Card */}
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-6'>
                    <div className='flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary text-xl font-semibold'>
                      {getInitials(customer.full_name)}
                    </div>
                    <div className='space-y-1'>
                      <CardTitle className='text-2xl'>
                        {customer.full_name ?? '-'}
                      </CardTitle>
                      <div className='flex items-center gap-2 text-muted-foreground'>
                        <Phone className='h-4 w-4' />
                        {customer.phone_number ?? '-'}
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Sheet open={isDebtSheetOpen} onOpenChange={(open) => { setIsDebtSheetOpen(open); if (!open) setDebtError(null) }}>
                      <SheetTrigger asChild>
                        <Button variant='outline' size='sm'>
                          <DollarSign className='mr-2 h-4 w-4' />
                          {t('customers.addDebt')}
                        </Button>
                      </SheetTrigger>
                      <SheetContent>
                        <SheetHeader>
                          <SheetTitle>{t('customers.addDebt')}</SheetTitle>
                          <SheetDescription>
                            {t('customers.addDebtDesc')}
                          </SheetDescription>
                        </SheetHeader>
                        <form onSubmit={handleCreateDebt} className='flex flex-col gap-4 p-4'>
                          {debtError && (
                            <div className='rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
                              {debtError}
                            </div>
                          )}
                          <div className='space-y-2'>
                            <Label htmlFor='debt_amount'>{t('customers.amount')}</Label>
                            <Input
                              id='debt_amount'
                              type='number'
                              placeholder='0.00'
                              value={debtAmount}
                              onChange={(e) => {
                                setDebtAmount(e.target.value)
                                setDebtError(null)
                              }}
                              required
                            />
                          </div>
                          <div className='space-y-2'>
                            <Label htmlFor='debt_description'>
                              {t('expenses.descriptionOptional')}
                            </Label>
                            <Textarea
                              id='debt_description'
                              placeholder={t('expenses.enterDescription')}
                              value={debtDescription}
                              onChange={(e) => setDebtDescription(e.target.value)}
                            />
                          </div>
                          <SheetFooter>
                            <Button type='submit' disabled={isCreatingDebt}>
                              {isCreatingDebt ? t('common.loading') : t('common.create')}
                            </Button>
                          </SheetFooter>
                        </form>
                      </SheetContent>
                    </Sheet>
                    <Sheet open={isIncomeSheetOpen} onOpenChange={setIsIncomeSheetOpen}>
                      <SheetTrigger asChild>
                        <Button variant='outline' size='sm'>
                          <DollarSign className='mr-2 h-4 w-4' />
                          {t('customers.addIncome')}
                        </Button>
                      </SheetTrigger>
                      <SheetContent>
                        <SheetHeader>
                          <SheetTitle>{t('customers.addIncome')}</SheetTitle>
                          <SheetDescription>
                            {t('customers.addIncomeDesc')}
                          </SheetDescription>
                        </SheetHeader>
                        <form onSubmit={handleCreateIncome} className='flex flex-col gap-4 p-4'>
                          <div className='space-y-2'>
                            <Label htmlFor='income_amount'>{t('customers.amount')}</Label>
                            <Input
                              id='income_amount'
                              type='number'
                              step='0.01'
                              placeholder='0.00'
                              value={incomeAmount}
                              onChange={(e) => setIncomeAmount(e.target.value)}
                              required
                            />
                          </div>
                          <div className='space-y-2'>
                            <Label htmlFor='income_description'>
                              {t('expenses.descriptionOptional')}
                            </Label>
                            <Textarea
                              id='income_description'
                              placeholder={t('expenses.enterDescription')}
                              value={incomeDescription}
                              onChange={(e) => setIncomeDescription(e.target.value)}
                            />
                          </div>
                          <SheetFooter>
                            <Button type='submit' disabled={isCreatingIncome}>
                              {isCreatingIncome ? t('common.loading') : t('common.create')}
                            </Button>
                          </SheetFooter>
                        </form>
                      </SheetContent>
                    </Sheet>
                    <Button variant='outline' size='sm' onClick={openEditSheet}>
                      <Pencil className='mr-2 h-4 w-4' />
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant='destructive'
                      size='sm'
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      <Trash2 className='mr-2 h-4 w-4' />
                      {t('common.delete')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Stats Cards */}
            <div className='grid gap-4 md:grid-cols-3'>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('customers.totalGiven')}</CardTitle>
                  <DollarSign className='h-4 w-4 text-green-500' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                    {formatCurrency(customer.total_given)}
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {t('customers.totalPaidByCustomer')}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('customers.amountOwed')}</CardTitle>
                  <DollarSign className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-2xl font-bold ${
                      customer.total_debt > 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {formatCurrency(customer.total_debt)}
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {t('customers.remainingBalance')}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('customers.totalOrders')}</CardTitle>
                  <ShoppingCart className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>
                    {customer.orders ?? 0}
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {t('customers.ordersPlaced')}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Details Card */}
            <div className='grid gap-4 md:grid-cols-2'>
              {/* Contact Info */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <User className='h-4 w-4' />
                    {t('customers.contactInfo')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('customers.fullName')}</p>
                      <p className='text-sm font-medium'>{customer.full_name ?? '-'}</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('customers.phoneNumber')}</p>
                      <p className='text-sm font-medium'>{customer.phone_number ?? '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timestamps */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <Calendar className='h-4 w-4' />
                    {t('customers.timestamps')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('customers.created')}</p>
                      <p className='text-sm font-medium'>{formatDate(customer.created_at)}</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('customers.lastUpdated')}</p>
                      <p className='text-sm font-medium'>{formatDate(customer.updated_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Orders Table */}
            <Card>
              <CardHeader>
                <CardTitle>{t('orders.title')}</CardTitle>
                <CardDescription>{t('customers.ordersDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>{t('customers.noOrders')}</p>
                ) : (
                  <div className='rounded-md border'>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID</TableHead>
                          <TableHead>{t('orders.price')}</TableHead>
                          <TableHead>{t('orders.createdAt')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell className='font-medium'>#{order.id}</TableCell>
                            <TableCell>{formatCurrency(order.full_price)}</TableCell>
                            <TableCell>{formatDate(order.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bought Products Table */}
            <Card>
              <CardHeader>
                <CardTitle>{t('customers.boughtProducts')}</CardTitle>
                <CardDescription>{t('customers.boughtProductsDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                {boughtProducts.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>{t('customers.noBoughtProducts')}</p>
                ) : (
                  <div className='rounded-md border'>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('products.thickness')}</TableHead>
                          <TableHead>{t('products.cell')}</TableHead>
                          <TableHead>{t('orders.dimensions')}</TableHead>
                          <TableHead>{t('products.quantity')}</TableHead>
                          <TableHead>{t('orders.price')}</TableHead>
                          <TableHead>{t('sell.kg')}</TableHead>
                          <TableHead>{t('orders.createdAt')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {boughtProducts.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell>{product.thickness}</TableCell>
                            <TableCell>{product.cell}</TableCell>
                            <TableCell>
                              {product.height} x {product.width}
                            </TableCell>
                            <TableCell>{product.quantity}</TableCell>
                            <TableCell>{formatCurrency(product.price)}</TableCell>
                            <TableCell>{product.weight}</TableCell>
                            <TableCell>{formatDate(product.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : notFound ? (
          <Card>
            <CardContent className='py-12 text-center'>
              <div className='flex flex-col items-center gap-4'>
                <div className='flex h-16 w-16 items-center justify-center rounded-full bg-muted'>
                  <UserX className='h-8 w-8 text-muted-foreground' />
                </div>
                <div className='space-y-2'>
                  <h3 className='text-lg font-semibold'>{t('customers.customerNotFound')}</h3>
                  <p className='text-sm text-muted-foreground'>
                    {t('customers.customerNotFoundDesc')}
                  </p>
                </div>
                <Button variant='outline' asChild className='mt-2'>
                  <Link to='/customers'>
                    <ArrowLeft className='mr-2 h-4 w-4' />
                    {t('customers.backToCustomers')}
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      {/* Edit Sheet */}
      <Sheet open={isEditSheetOpen} onOpenChange={setIsEditSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('customers.editCustomer')}</SheetTitle>
            <SheetDescription>
              {t('customers.updateCustomerInfo')}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleUpdate} className='flex flex-col gap-4 p-4'>
            <div className='space-y-2'>
              <Label htmlFor='edit_full_name'>{t('customers.fullName')}</Label>
              <Input
                id='edit_full_name'
                placeholder={t('customers.enterFullName')}
                value={formData.full_name}
                onChange={(e) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='edit_phone_number'>{t('customers.phoneNumber')}</Label>
              <Input
                id='edit_phone_number'
                type='tel'
                placeholder='+998901234567'
                value={formData.phone_number}
                onChange={(e) =>
                  setFormData({ ...formData, phone_number: e.target.value })
                }
                required
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='edit_total_given'>{t('customers.totalGiven')}</Label>
              <Input
                id='edit_total_given'
                type='number'
                step='0.01'
                placeholder='0.00'
                value={formData.total_given ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    total_given: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='edit_total_debt'>{t('customers.amountOwed')}</Label>
              <Input
                id='edit_total_debt'
                type='number'
                step='0.01'
                placeholder='0.00'
                value={formData.total_debt ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    total_debt: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
              />
            </div>
            <SheetFooter>
              <Button type='submit' disabled={isUpdating}>
                {isUpdating ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workers.areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('customers.deleteCustomerConfirmation', { name: customer?.full_name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className='bg-destructive hover:bg-destructive/90 cursor-pointer'
            >
              {isDeleting ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
